/**
 * PlatformAdmin.jsx — Platform admin dashboard.
 *
 * Access gate: renders a 403 placeholder if the caller is not a
 * platform admin (as reported by `GET /api/tenants/me/memberships`).
 * Every request that follows is gated server-side too — the UI gate is
 * purely a belt-and-braces UX nicety.
 *
 * Layout (2-column on lg+, stacked on sm):
 *   ┌────────────────┐ ┌───────────────────────────────────────┐
 *   │ search + filter │ │ tenant detail                         │
 *   │ + create button │ │ (members · fhir · audit · actions)    │
 *   │ stats strip     │ │ safe impersonation entry              │
 *   │ tenant list     │ └───────────────────────────────────────┘
 *   └────────────────┘
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import * as adminApi from '../lib/api/adminApi';
import * as tenantApi from '../lib/api/tenantApi';
import {
  Building2, Search, Plus, Pause, Play, Archive, Trash2, Eye, EyeOff,
  AlertTriangle, Shield, RefreshCw, Loader2, Users, Database,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TONES = {
  active:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  suspended: 'bg-amber-50 text-amber-800 border-amber-200',
  archived:  'bg-slate-100 text-slate-600 border-slate-200',
};
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

// ─── Gate wrapper ──────────────────────────────────────────────────
export default function PlatformAdminPage() {
  const { isPlatformAdmin, loading } = useTenant();
  if (loading) {
    return (
      <div data-testid="platform-admin-loading" className="p-12 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading platform admin…
      </div>
    );
  }
  if (!isPlatformAdmin) {
    return (
      <div data-testid="platform-admin-forbidden" className="p-12 max-w-lg mx-auto text-center">
        <Shield className="w-10 h-10 mx-auto mb-3 text-rose-400" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">Platform-admin access required</h1>
        <p className="text-sm text-slate-600">
          This page is restricted to platform administrators. Contact your operations team if you believe this is in error.
        </p>
      </div>
    );
  }
  return <PlatformAdminInner />;
}

// ─── Inner (only mounts for platform admins) ───────────────────────
function PlatformAdminInner() {
  const navigate = useNavigate();
  const { beginImpersonation, isImpersonating, activeTenant } = useTenant();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingHardDelete, setPendingHardDelete] = useState(null);
  const [busy, setBusy] = useState(null);

  const refreshList = useCallback(async () => {
    setLoading(true);
    const [list, st] = await Promise.all([
      adminApi.listAllTenants({ q: query, status: statusFilter }),
      adminApi.getPlatformStats(),
    ]);
    if (list.ok) setTenants(list.data?.tenants || []);
    else          toast.error(list.error?.message || 'Failed to load tenants');
    if (st.ok)   setStats(st.data);
    setLoading(false);
  }, [query, statusFilter]);

  useEffect(() => { refreshList(); }, [refreshList]);

  const refreshDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    const r = await adminApi.getTenantDetail(id);
    if (r.ok) setDetail(r.data);
    else      toast.error(r.error?.message || 'Failed to load detail');
    setDetailLoading(false);
  }, []);

  useEffect(() => { refreshDetail(selectedId); }, [selectedId, refreshDetail]);

  const runAction = async (action, id, fn, okMsg) => {
    setBusy(`${id}-${action}`);
    const r = await fn();
    setBusy(null);
    if (r.ok) { toast.success(okMsg); await refreshList(); await refreshDetail(id); }
    else       toast.error(r.error?.message || `${action} failed`);
  };

  const onSuspend    = (t) => runAction('suspend',    t.id, () => adminApi.suspendTenant(t.id),    'Tenant suspended');
  const onReactivate = (t) => runAction('reactivate', t.id, () => adminApi.reactivateTenant(t.id), 'Tenant reactivated');
  const onArchive    = (t) => {
    if (!window.confirm(`Soft-delete ${t.slug}? The tenant row stays in Mongo for recovery but members lose access.`)) return;
    return runAction('archive', t.id, () => adminApi.archiveTenant(t.id), 'Tenant archived');
  };

  const confirmHardDelete = async () => {
    if (!pendingHardDelete) return;
    const { tenant, confirmSlug } = pendingHardDelete;
    if (confirmSlug !== tenant.slug) {
      toast.error('Slug does not match');
      return;
    }
    setBusy(`${tenant.id}-hard-delete`);
    const r = await adminApi.hardDeleteTenant(tenant.id, tenant.slug);
    setBusy(null);
    if (r.ok) {
      toast.success(`Deleted — cascaded ${r.data?.cascaded_members || 0} members, ${r.data?.cascaded_subscriptions || 0} subscriptions`);
      setPendingHardDelete(null);
      setSelectedId(null);
      setDetail(null);
      refreshList();
    } else {
      toast.error(r.error?.message || 'Hard delete failed');
    }
  };

  const onImpersonate = async (t) => {
    const r = await adminApi.startImpersonation(t.id);
    if (!r.ok) { toast.error(r.error?.message || 'Impersonation failed'); return; }
    beginImpersonation(t.id, t.name, t.slug);
    toast.success(`Viewing as ${t.name}`);
    navigate('/lumina/tenants/admin');
  };

  const selected = useMemo(() => tenants.find((t) => t.id === selectedId) || null, [tenants, selectedId]);

  return (
    <div data-testid="platform-admin" className="p-6 max-w-7xl mx-auto">
      {isImpersonating && activeTenant && (
        <ImpersonationBanner tenant={activeTenant} />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" /> Platform Admin
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage every tenant on the platform — create, suspend, archive, hard-delete, and safely impersonate.
          </p>
        </div>
        <button
          data-testid="platform-admin-create-btn"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> New tenant
        </button>
      </div>

      {/* Stats */}
      {stats && <StatsStrip stats={stats} />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm flex-1 min-w-[240px]">
          <div className="flex items-center gap-1 flex-1 border border-slate-300 rounded px-2 bg-white">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              data-testid="platform-admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search by name or slug"
              className="flex-1 py-1.5 text-sm outline-none"
            />
          </div>
        </label>
        <select
          data-testid="platform-admin-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          data-testid="platform-admin-refresh"
          onClick={refreshList}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <TenantList
            tenants={tenants}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={loading}
          />
        </div>
        <div className="lg:col-span-3">
          {selected ? (
            <TenantDetail
              tenant={selected}
              detail={detail}
              loading={detailLoading}
              busy={busy}
              onSuspend={onSuspend}
              onReactivate={onReactivate}
              onArchive={onArchive}
              onHardDelete={(t) => setPendingHardDelete({ tenant: t, confirmSlug: '' })}
              onImpersonate={onImpersonate}
            />
          ) : (
            <div data-testid="platform-admin-no-selection"
              className="p-12 text-center text-slate-500 border border-dashed border-slate-300 rounded">
              <Eye className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              Select a tenant to view details.
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateTenantModal
          onCancel={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshList(); }}
        />
      )}
      {pendingHardDelete && (
        <HardDeleteModal
          state={pendingHardDelete}
          onUpdate={setPendingHardDelete}
          onCancel={() => setPendingHardDelete(null)}
          onConfirm={confirmHardDelete}
          busy={busy === `${pendingHardDelete.tenant.id}-hard-delete`}
        />
      )}
    </div>
  );
}

// ─── Components ───────────────────────────────────────────────────
function StatsStrip({ stats }) {
  const cards = [
    { label: 'Active',     value: stats.tenants.active,    tone: 'emerald', icon: CheckCircle2, testid: 'platform-admin-stat-active' },
    { label: 'Suspended',  value: stats.tenants.suspended, tone: 'amber',   icon: Pause,        testid: 'platform-admin-stat-suspended' },
    { label: 'Archived',   value: stats.tenants.archived,  tone: 'slate',   icon: Archive,      testid: 'platform-admin-stat-archived' },
    { label: 'Members',    value: stats.members,           tone: 'indigo',  icon: Users,        testid: 'platform-admin-stat-members' },
    { label: 'Subscriptions', value: stats.subscriptions,  tone: 'indigo',  icon: Database,     testid: 'platform-admin-stat-subs' },
  ];
  const tones = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="platform-admin-stats">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid}
          className={`p-3 rounded-lg border ${tones[c.tone]} flex items-center gap-2`}>
          <c.icon className="w-5 h-5 opacity-80" />
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-80">{c.label}</div>
            <div className="text-xl font-bold">{c.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TenantList({ tenants, selectedId, onSelect, loading }) {
  if (loading && tenants.length === 0) {
    return <div data-testid="platform-admin-list-loading" className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;
  }
  if (tenants.length === 0) {
    return (
      <div data-testid="platform-admin-empty"
        className="p-8 text-center text-slate-500 border border-dashed border-slate-300 rounded">
        <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-400" /> No tenants match.
      </div>
    );
  }
  return (
    <div data-testid="platform-admin-list" className="border border-slate-200 rounded overflow-hidden">
      {tenants.map((t) => (
        <button
          key={t.id}
          data-testid={`platform-admin-list-row-${t.slug}`}
          onClick={() => onSelect(t.id)}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${selectedId === t.id ? 'bg-indigo-50/60' : ''}`}
        >
          <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-sm truncate">{t.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{t.slug}</div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_TONES[t.status] || STATUS_TONES.archived}`}>{t.status}</span>
          <span className="text-[10px] text-slate-500 ml-1" title="member count">{t.member_count || 0}m</span>
        </button>
      ))}
    </div>
  );
}

function TenantDetail({ tenant, detail, loading, busy, onSuspend, onReactivate, onArchive, onHardDelete, onImpersonate }) {
  if (loading && !detail) return <div data-testid="platform-admin-detail-loading" className="p-8 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>;
  const cfg = detail?.tenant?.fhir_config || {};
  return (
    <div data-testid="platform-admin-detail" className="border border-slate-200 rounded p-4 bg-white">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900" data-testid="platform-admin-detail-name">{tenant.name}</h2>
          <div className="text-xs text-slate-500 font-mono">{tenant.id}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_TONES[tenant.status] || STATUS_TONES.archived}`} data-testid="platform-admin-detail-status">
          {tenant.status}
        </span>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tenant.status === 'active' ? (
          <ActionButton testid={`platform-admin-suspend-${tenant.slug}`} icon={Pause}    label="Suspend"    busy={busy === `${tenant.id}-suspend`}    onClick={() => onSuspend(tenant)} />
        ) : tenant.status === 'suspended' ? (
          <ActionButton testid={`platform-admin-reactivate-${tenant.slug}`} icon={Play} label="Reactivate" busy={busy === `${tenant.id}-reactivate`} onClick={() => onReactivate(tenant)} />
        ) : null}
        {tenant.status !== 'archived' && (
          <ActionButton testid={`platform-admin-archive-${tenant.slug}`} icon={Archive} label="Archive (soft)" busy={busy === `${tenant.id}-archive`} tone="slate" onClick={() => onArchive(tenant)} />
        )}
        <ActionButton testid={`platform-admin-impersonate-${tenant.slug}`} icon={Eye}    label="View as tenant" tone="indigo" onClick={() => onImpersonate(tenant)} />
        <ActionButton testid={`platform-admin-hard-delete-${tenant.slug}`} icon={Trash2} label="Hard delete"    tone="rose"   onClick={() => onHardDelete(tenant)} />
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-4">
        <Kv label="Slug" value={tenant.slug} />
        <Kv label="Members" value={tenant.member_count || 0} testid="platform-admin-detail-member-count" />
        <Kv label="Subscriptions" value={tenant.subscription_count || 0} testid="platform-admin-detail-sub-count" />
        <Kv label="Created" value={tenant.created_at} />
        <Kv label="FHIR base URL" value={cfg.base_url || '—'} />
        <Kv label="Auth mode" value={cfg.auth_mode || 'none'} />
      </div>

      {/* Members preview */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Members ({detail?.members?.length || 0})</h3>
        <div className="border border-slate-200 rounded overflow-hidden" data-testid="platform-admin-detail-members">
          {(detail?.members || []).slice(0, 6).map((m) => (
            <div key={m.user_id} className="flex items-center gap-2 px-2 py-1 text-xs border-b border-slate-100 last:border-b-0">
              <span className="flex-1 truncate">{m.email || <em className="text-slate-400">—</em>}</span>
              <code className="text-[10px] bg-slate-100 px-1 rounded capitalize">{m.role}</code>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {m.status}
              </span>
            </div>
          ))}
          {(!detail?.members || detail.members.length === 0) && (
            <div className="p-3 text-xs text-slate-500 text-center">No members.</div>
          )}
        </div>
      </div>

      {/* Audit preview */}
      <div>
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Recent audit</h3>
        <div className="border border-slate-200 rounded overflow-hidden" data-testid="platform-admin-detail-audit">
          {(detail?.audit_preview || []).slice(0, 8).map((e) => (
            <div key={e.id} className="px-2 py-1 text-xs border-b border-slate-100 last:border-b-0">
              <code className="text-[10px] bg-slate-100 px-1 rounded">{e.action}</code>
              <span className="ml-2 text-slate-500">{e.target_type} · {e.target_id}</span>
              <span className="float-right text-slate-400 text-[10px]">{e.at}</span>
            </div>
          ))}
          {(!detail?.audit_preview || detail.audit_preview.length === 0) && (
            <div className="p-3 text-xs text-slate-500 text-center">No audit entries.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, busy, testid, tone = 'indigo' }) {
  const tones = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    slate:  'bg-slate-200 hover:bg-slate-300 text-slate-900',
    rose:   'bg-rose-600 hover:bg-rose-700 text-white',
    amber:  'bg-amber-500 hover:bg-amber-600 text-white',
  };
  return (
    <button
      data-testid={testid}
      disabled={busy}
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold disabled:opacity-50 ${tones[tone]}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}

function Kv({ label, value, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 truncate" data-testid={testid} title={value}>{String(value)}</span>
    </div>
  );
}

// ─── Create tenant modal ───────────────────────────────────────────
function CreateTenantModal({ onCancel, onCreated }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await tenantApi.createTenant({ name, slug: slug.toLowerCase() });
    setBusy(false);
    if (r.ok) { toast.success('Tenant created'); onCreated(); }
    else       toast.error(r.error?.message || 'Create failed');
  };

  return (
    <div
      data-testid="platform-admin-create-modal"
      className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center"
    >
      <form onSubmit={onSubmit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Create new tenant</h3>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-700">Name</label>
          <input
            data-testid="platform-admin-create-name"
            required value={name} onChange={(e) => setName(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded text-sm"
            placeholder="Demo Clinic"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-700">Slug</label>
          <input
            data-testid="platform-admin-create-slug"
            required value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="^[a-z0-9][a-z0-9\-]*[a-z0-9]$"
            title="lowercase, numbers, hyphens; min 2 chars"
            className="px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
            placeholder="demo-clinic"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button" data-testid="platform-admin-create-cancel"
            onClick={onCancel}
            className="px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit" data-testid="platform-admin-create-submit"
            disabled={busy}
            className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Hard-delete modal — slug-echo confirm ─────────────────────────
function HardDeleteModal({ state, onUpdate, onCancel, onConfirm, busy }) {
  const { tenant, confirmSlug } = state;
  const match = confirmSlug === tenant.slug;
  return (
    <div
      data-testid="platform-admin-hard-delete-modal"
      className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-3">
        <div className="flex items-center gap-2 text-rose-700">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="text-lg font-semibold">Hard delete — irreversible</h3>
        </div>
        <p className="text-xs text-slate-600">
          This permanently removes the tenant, all memberships, and every FHIR subscription bucketed under <code className="bg-slate-100 px-1 rounded">{tenant.slug}</code>. This action cannot be undone.
        </p>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-700">
            Type the slug <code className="bg-slate-100 px-1 rounded">{tenant.slug}</code> to confirm:
          </label>
          <input
            data-testid="platform-admin-hard-delete-confirm-input"
            value={confirmSlug}
            onChange={(e) => onUpdate({ tenant, confirmSlug: e.target.value })}
            className="px-2 py-1.5 border border-rose-300 rounded text-sm font-mono"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button data-testid="platform-admin-hard-delete-cancel"
            onClick={onCancel}
            className="px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
          >
            Cancel
          </button>
          <button
            data-testid="platform-admin-hard-delete-confirm"
            disabled={!match || busy}
            onClick={onConfirm}
            className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete forever
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Impersonation banner (shared by App) ──────────────────────────
export function ImpersonationBanner({ tenant }) {
  const { endImpersonation, impersonation } = useTenant();
  const [stopping, setStopping] = useState(false);
  const onExit = async () => {
    setStopping(true);
    try {
      if (impersonation?.tenant_id) await adminApi.stopImpersonation(impersonation.tenant_id);
    } catch { /* non-fatal */ }
    endImpersonation();
    setStopping(false);
  };
  return (
    <div
      data-testid="impersonation-banner"
      className="mb-4 p-3 rounded border-2 border-rose-400 bg-rose-50 text-rose-900 flex items-center gap-3"
    >
      <EyeOff className="w-5 h-5" />
      <div className="flex-1 text-sm">
        <strong>Impersonating:</strong> viewing as tenant <code className="bg-rose-100 px-1 rounded">{tenant.tenant_slug || tenant.tenant_name}</code>. All actions will be recorded against your platform-admin identity.
      </div>
      <button
        data-testid="impersonation-exit-btn"
        onClick={onExit}
        disabled={stopping}
        className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1"
      >
        {stopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
        Exit impersonation
      </button>
    </div>
  );
}
